import React, { useState, useMemo } from 'react';
import { X, Download, FileText, Search, History, AlertCircle, Trash2 } from 'lucide-react';
import { useAuditTrail } from '../../../../hooks/useAuditTrail';
import { useVessel } from '../../../../contexts/VesselContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import { exportAuditLogToPDF } from '../../../../utils/pdf/auditExportPDF';
import { exportAuditLogToCSV } from '../../../../utils/auditExportCSV';

const ChangeLogViewer = ({ onBack }) => {
  // FT-515: getChanges now reads from async-hydrated storage state; isLoading
  // is exposed so this viewer can show a loading state instead of flashing
  // "No change history yet" before the storage read resolves.
  const { getChanges, isLoading } = useAuditTrail('equipment');
  const { currentVesselId, getCurrentVessel } = useVessel();
  const { currentUser } = usePermissions();
  const currentVessel = getCurrentVessel();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');

  // Get all audit entries
  const allEntries = useMemo(() => {
    return getChanges();
  }, [getChanges]);

  // Apply filters
  const filteredEntries = useMemo(() => {
    let entries = [...allEntries];

    // Search filter
    if (searchTerm) {
      entries = entries.filter(entry =>
        entry.item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.item?.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.reason?.label?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Action filter
    if (filterAction !== 'all') {
      entries = entries.filter(entry => entry.action === filterAction);
    }

    // Date range filter
    if (filterDateRange !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();

      switch (filterDateRange) {
        case '7days':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case '30days':
          cutoffDate.setDate(now.getDate() - 30);
          break;
        case '90days':
          cutoffDate.setDate(now.getDate() - 90);
          break;
        default:
          break;
      }

      entries = entries.filter(entry => new Date(entry.timestamp) >= cutoffDate);
    }

    return entries;
  }, [allEntries, searchTerm, filterAction, filterDateRange]);

  // FT-512: Export to CSV — delegate to the shared compliance utility so all
  // audit CSV exports share one code path (same pattern as exportAuditLogToPDF).
  // Pass the already-loaded filtered entries explicitly to avoid a second load.
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const handleExport = async () => {
    if (filteredEntries.length === 0 || isExportingCSV) return;
    setIsExportingCSV(true);
    try {
      await exportAuditLogToCSV(
        currentVesselId,
        'equipment',
        filteredEntries,
        currentVessel || {}
      );
    } finally {
      setIsExportingCSV(false);
    }
  };

  // FT-512: Export to MCA-compliant PDF (with hash-verification banner).
  // Pass the already-loaded, filtered entries explicitly so the exporter's
  // internal load path is never used. exportAuditLogToPDF tolerates the nested
  // entry shape (entry.user.name / entry.item.name) produced by useAuditTrail.
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const handleExportPDF = async () => {
    if (filteredEntries.length === 0 || isExportingPDF) return;
    setIsExportingPDF(true);
    try {
      await exportAuditLogToPDF(
        currentVesselId,
        'equipment',
        filteredEntries,
        currentUser,
        currentVessel || {}
      );
    } finally {
      setIsExportingPDF(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Unknown';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 2026-05-31: action label now distinguishes soft delete (Archived) vs hard
  // delete (Deleted Permanently). Previously both showed as 'Deleted' with a
  // tiny "(Recoverable)" / "(Permanent)" text suffix that was easy to miss.
  const getActionBadge = (action, deleteType) => {
    if (action === 'delete') {
      if (deleteType === 'soft') {
        return { label: 'Archived', color: 'bg-yellow-100 text-yellow-800', icon: Trash2 };
      } else if (deleteType === 'hard') {
        return { label: 'Deleted Permanently', color: 'bg-red-200 text-red-900', icon: Trash2 };
      }
      // No deleteType captured (legacy entry) — keep generic
      return { label: 'Deleted', color: 'bg-red-100 text-red-800', icon: Trash2 };
    }
    const badges = {
      delete_unit: { label: 'Unit Deleted', color: 'bg-orange-100 text-orange-800', icon: Trash2 },
      restore: { label: 'Restored', color: 'bg-green-100 text-green-800', icon: History },
      created: { label: 'Created', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
      // FT-531: field-level equipment edits now log action: 'update'. Without
      // this entry, getActionBadge's fallback would show the literal string
      // "update" (lowercase) with a generic grey badge.
      update: { label: 'Updated', color: 'bg-cyan-100 text-cyan-800', icon: FileText }
    };
    return badges[action] || { label: action, color: 'bg-gray-100 text-gray-800', icon: AlertCircle };
  };

  // No longer needed — soft/hard distinction is now part of the action label.
  // Retained for backward compatibility with any external callers.
  const getDeleteTypeBadge = () => null;

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Filter Section - 3-row template */}
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="space-y-3">
          {/* Row 1: Title + Export + Close */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Equipment Change History</h3>
            <div className="flex items-center gap-2">
              {/* Export CSV Button */}
              <button
                onClick={handleExport}
                disabled={filteredEntries.length === 0 || isExportingCSV}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
              {/* FT-512: Export PDF Button (MCA-compliant, hash-verified) */}
              <button
                onClick={handleExportPDF}
                disabled={filteredEntries.length === 0 || isExportingPDF}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="h-4 w-4" />
                Export PDF
              </button>
              {/* Close Button */}
              <button
                onClick={onBack}
                className="inline-flex items-center justify-center p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Row 2: Search + Action Filter + Date Range */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search items, users, reasons..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600"
              />
            </div>

            {/* Action Type + Date Range Filters */}
            <div className="flex gap-2">
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 min-w-[140px]"
              >
                <option value="all">All Actions</option>
                <option value="created">Created</option>
                <option value="update">Updated</option>
                <option value="delete">Deleted</option>
                <option value="delete_unit">Unit Deleted</option>
                <option value="restore">Restored</option>
              </select>
              <select
                value={filterDateRange}
                onChange={(e) => setFilterDateRange(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 min-w-[140px]"
              >
                <option value="all">All Time</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
              </select>
            </div>
          </div>

          {/* Row 3: Entry Count */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'}
              {searchTerm || filterAction !== 'all' || filterDateRange !== 'all' ? ' (filtered)' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 pb-24 md:pb-6">
        {isLoading ? (
          <div className="text-center py-12">
            <History className="h-12 w-12 text-gray-300 mx-auto mb-3 animate-pulse" />
            <p className="text-gray-500">Loading change history…</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12">
            <History className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {allEntries.length === 0 ? 'No change history yet' : 'No entries match your filters'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Item Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Details</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Timestamp</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredEntries.map((entry, index) => {
                  const badge = getActionBadge(entry.action, entry.deleteType);
                  const hasDetails = !!(entry.reason || entry.authorizedBy || entry.changes?.after);
                  return (
                    <tr key={entry.id || index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700 align-top">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                          {badge.label}
                        </span>
                        {/* FT-530: soft/hard delete is already distinguished in the
                            Action badge label above (Archived vs Deleted
                            Permanently) — getDeleteTypeBadge is a retained no-op,
                            called here only so its call site (and therefore its
                            definition) stays live if a future entry shape needs
                            a separate badge. */}
                        {getDeleteTypeBadge(entry.deleteType)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 align-top">
                        <div>{entry.item?.name || entry.item?.type || 'Unknown Item'}</div>
                        {/* FIX-FIRST (review of abf00555): Type/Category/Location/
                            Serial Number are point-in-time historical facts
                            captured into the entry at write time (see
                            useAuditTrail.js) specifically because they are NOT
                            recoverable anywhere else once an item is permanently
                            deleted, or later edited — neither export (CSV/PDF)
                            carries these fields, and the equipment detail page
                            only ever shows CURRENT values (and doesn't exist at
                            all for a deleted item). Shown as a compact second
                            line in this cell rather than a new column, so the
                            table stays one row per entry. */}
                        {(entry.item?.type || entry.item?.category || entry.item?.location || entry.item?.serialNumber) && (
                          <div className="mt-0.5 text-xs text-gray-500">
                            {[
                              entry.item?.type && `Type: ${entry.item.type}`,
                              entry.item?.category && `Category: ${entry.item.category}`,
                              entry.item?.location && `Location: ${entry.item.location}`,
                              entry.item?.serialNumber && `Serial: ${entry.item.serialNumber}`,
                            ].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      {/* FT-531 detail (Reason / Authorized By / field-level
                          Changes) re-housed from the old card layout into this
                          column so the view stays a single row per entry,
                          matching the standardized table idiom used by every
                          other FT-515/554-converted history view. */}
                      <td className="px-4 py-3 text-sm text-gray-700 align-top">
                        {!hasDetails ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <div className="space-y-1">
                            {entry.reason && (
                              <div>
                                <span className="text-gray-500">Reason:</span>{' '}
                                <span className="text-gray-900">{entry.reason.label}</span>
                                {entry.reason.notes && (
                                  <div className="mt-0.5 text-gray-600 italic">"{entry.reason.notes}"</div>
                                )}
                              </div>
                            )}
                            {entry.authorizedBy && (
                              <div>
                                <span className="text-gray-500">Authorized by:</span>{' '}
                                <span className="text-gray-900">{entry.authorizedBy}</span>
                              </div>
                            )}
                            {/* FT-531: field-level edit detail. entry.changes is
                                { before: {...}, after: {...} } — iterate the AFTER
                                side's keys only (matches the Vessel Settings lesson
                                of diffing the after-side keys, not a union of both),
                                since this logging path only ever records ONE changed
                                field per entry, there's exactly one key here. Sparse
                                values (null — see toAuditValue in EquipmentDetailView,
                                normalized from undefined for safe JSON.stringify in
                                the CSV export — and undefined, handled defensively)
                                render as "(not set)", never blank or literal
                                "undefined". */}
                            {entry.changes?.after && (
                              <div>
                                <span className="text-gray-500">Changes:</span>
                                <div className="mt-0.5 space-y-0.5">
                                  {Object.keys(entry.changes.after).map(key => {
                                    const beforeVal = entry.changes.before?.[key];
                                    const afterVal = entry.changes.after[key];
                                    const formatVal = (v) => (v === null || v === undefined) ? '(not set)' : String(v);
                                    return (
                                      <div key={key} className="text-gray-900">
                                        <span className="font-medium">{key}:</span>{' '}
                                        <span className="text-gray-600">{formatVal(beforeVal)}</span>
                                        {' → '}
                                        <span className="text-gray-900">{formatVal(afterVal)}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 align-top">
                        {entry.user?.name || 'Unknown'}
                        {entry.user?.role ? ` (${entry.user.role})` : ''}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 align-top">
                        {formatDate(entry.timestamp)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] z-50 shadow-lg">
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={filteredEntries.length === 0 || isExportingCSV}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            CSV
          </button>
          {/* FT-512: Export PDF (MCA-compliant, hash-verified) */}
          <button
            onClick={handleExportPDF}
            disabled={filteredEntries.length === 0 || isExportingPDF}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText className="h-4 w-4" />
            PDF
          </button>
          <button
            onClick={onBack}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeLogViewer;
